'use client';

import { useInfiniteQuery } from '@tanstack/react-query';

import {
  AuthInfiniteQueryOptions,
  UnauthInfiniteQueryOptions,
  WELLO_APP_PUSH_PARENT_CATEGORY_TYPE,
} from '@/api';
import { PageNavigation } from '@/components/client';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useStatusBarColor } from '@/hooks/useStatusBarColor';
import { useAuthStore } from '@/stores/AuthStore';
import { STATUS_COLOR, useDeviceInfoStore } from '@/stores/DeviceStore';

const INITIAL_PAGE = 1;
const PAGE_SIZE = 20;

export const HomeNavigation = () => {
  const isLogin = useAuthStore((state) => state.isLogin);
  const { isWelloApp } = useCheckWelloApp();
  const fcmToken = useDeviceInfoStore(
    (state) => state.deviceTokenData?.fcm_token ?? '',
  );

  const policyAppPushQuery = useInfiniteQuery({
    ...(isLogin
      ? AuthInfiniteQueryOptions['get:/wello/v2/auth/wello-app-push']
      : UnauthInfiniteQueryOptions['get:/wello/v2/allows/wello-app-push'])({
      query: {
        fcmToken: fcmToken,
        page: INITIAL_PAGE,
        size: PAGE_SIZE,
        welloAppPushParentCategoryTypeCd:
          WELLO_APP_PUSH_PARENT_CATEGORY_TYPE.POLICY,
      },
    }),
    enabled: isLogin !== undefined,
    getNextPageParam: (lastPage) => {
      if (lastPage.context?.totalCount && lastPage.context.page) {
        const nextPage = lastPage.context.page + 1;
        const maxPage = Math.ceil(lastPage.context.totalCount / PAGE_SIZE);

        return nextPage <= maxPage ? nextPage : undefined;
      }
    },

    initialPageParam: INITIAL_PAGE,
  });

  const communityAppPushQuery = useInfiniteQuery({
    ...(isLogin
      ? AuthInfiniteQueryOptions['get:/wello/v2/auth/wello-app-push']
      : UnauthInfiniteQueryOptions['get:/wello/v2/allows/wello-app-push'])({
      query: {
        fcmToken: fcmToken,
        page: INITIAL_PAGE,
        size: PAGE_SIZE,
        welloAppPushParentCategoryTypeCd:
          WELLO_APP_PUSH_PARENT_CATEGORY_TYPE.COMMUNITY,
      },
    }),
    enabled: isLogin !== undefined,
    getNextPageParam: (lastPage) => {
      if (lastPage.context?.totalCount && lastPage.context.page) {
        const nextPage = lastPage.context.page + 1;
        const maxPage = Math.ceil(lastPage.context.totalCount / PAGE_SIZE);

        return nextPage <= maxPage ? nextPage : undefined;
      }
    },

    initialPageParam: INITIAL_PAGE,
  });

  const serviceAppPushQuery = useInfiniteQuery({
    ...(isLogin
      ? AuthInfiniteQueryOptions['get:/wello/v2/auth/wello-app-push']
      : UnauthInfiniteQueryOptions['get:/wello/v2/allows/wello-app-push'])({
      query: {
        fcmToken: fcmToken,
        page: INITIAL_PAGE,
        size: PAGE_SIZE,
        welloAppPushParentCategoryTypeCd:
          WELLO_APP_PUSH_PARENT_CATEGORY_TYPE.SERVICE,
      },
    }),
    enabled: isLogin !== undefined,
    getNextPageParam: (lastPage) => {
      if (lastPage.context?.totalCount && lastPage.context.page) {
        const nextPage = lastPage.context.page + 1;
        const maxPage = Math.ceil(lastPage.context.totalCount / PAGE_SIZE);

        return nextPage <= maxPage ? nextPage : undefined;
      }
    },

    initialPageParam: INITIAL_PAGE,
  });

  const hasUnreadNotifications = (query: typeof policyAppPushQuery) =>
    query.data?.pages?.[0]?.context?.contents?.some((item) => !item.readYN);

  const showNotificationDot =
    hasUnreadNotifications(policyAppPushQuery) ||
    hasUnreadNotifications(communityAppPushQuery) ||
    hasUnreadNotifications(serviceAppPushQuery);

  useStatusBarColor({
    color: STATUS_COLOR.HOME,
  });

  return (
    <PageNavigation
      logo
      backButton={false}
      bellButton={isWelloApp}
      backgroundColor="gray"
      notification={showNotificationDot}
      isNotUsedHomeButton
    />
  );
};
